<script setup>
import { RouterLink } from 'vue-router'
import { ShieldCheckIcon, UserGroupIcon, DocumentTextIcon, ArrowRightIcon, CheckIcon } from '@heroicons/vue/24/outline'
import TrustBadge from '@/components/ui/TrustBadge.vue'
import FloatingCard from '@/components/ui/FloatingCard.vue'
import { PRICING } from '@/utils/constants/pricing.js'

/**
 * LandingHero V3 - Storytelling Visual Design
 *
 * Migrated from public/prototypes/landing-v3-storytelling.html
 * Following MIGRATION_LANDING_V3.md guidelines
 *
 * Features:
 * - Grid 7/5 asymmetric layout (content/visual)
 * - Organic blob background with CSS pseudo-elements
 * - TrustBadge GDPR compliance indicator
 * - Headline with decorative underline SVG
 * - Floating animated service cards
 * - Trust guarantee badge
 * - Responsive mobile-first design
 */

const floatingCards = [
  {
    icon: ShieldCheckIcon,
    title: PRICING.OLVIDO.name,
    subtitle: `${PRICING.OLVIDO.price}€`,
    delay: 0
  },
  {
    icon: UserGroupIcon,
    title: PRICING.OLVIDO_POSTMORTEM.name,
    subtitle: `${PRICING.OLVIDO_POSTMORTEM.price}€`,
    delay: 1
  },
  {
    icon: DocumentTextIcon,
    title: PRICING.TESTAMENTO.name,
    subtitle: `${PRICING.TESTAMENTO.price}€`,
    delay: 2
  }
]

const scrollToSection = (href) => {
  const element = document.querySelector(href)
  if (element) {
    element.scrollIntoView({ behavior: 'smooth' })
  }
}
</script>

<template>
  <section class="bg-organic min-h-screen flex items-center pt-24 pb-16">
    <div class="max-w-7xl mx-auto px-6 relative z-10">
      <div class="grid lg:grid-cols-12 gap-12 items-center">

        <!-- Left Content (7 columns) -->
        <div class="lg:col-span-7">
          <!-- Badge -->
          <TrustBadge
            label="Conforme a GDPR Art. 17"
            sublabel="Derecho de Supresion"
            class="animate-fade-up mb-8"
          />

          <!-- Headline -->
          <h1 class="animate-fade-up delay-100 text-5xl md:text-6xl lg:text-7xl font-bold text-text-primary leading-[1.05] mb-8">
            Tu información,
            <span class="relative">
              <span class="relative z-10">tu derecho</span>
              <svg class="absolute -bottom-2 left-0 w-full h-4 text-accent-400/30" viewBox="0 0 200 20" preserveAspectRatio="none">
                <path d="M0 15 Q50 5 100 15 T200 15" stroke="currentColor" stroke-width="8" fill="none" stroke-linecap="round"/>
              </svg>
            </span>
            a olvidar
          </h1>

          <!-- Subtitle -->
          <p class="animate-fade-up delay-200 text-xl text-text-secondary leading-relaxed mb-10 max-w-xl">
            Eliminamos tu información personal de Internet de forma legal. Ejercicio profesional del derecho al olvido conforme al RGPD.
          </p>

          <!-- CTA + Trust -->
          <div class="animate-fade-up delay-300 flex flex-col sm:flex-row items-start sm:items-center gap-6">
            <a
              href="#precios"
              class="group inline-flex items-center gap-3 px-8 py-4 bg-text-primary text-white font-semibold rounded-2xl hover:bg-slate-800 transition-all cursor-pointer"
              @click.prevent="scrollToSection('#precios')"
            >
              Ver planes y precios
              <span class="w-8 h-8 bg-white/10 rounded-full flex items-center justify-center group-hover:bg-white/20 transition-colors">
                <ArrowRightIcon class="w-4 h-4" />
              </span>
            </a>

            <div class="flex items-center gap-4 text-sm text-text-tertiary">
              <div class="flex items-center gap-2">
                <CheckIcon class="w-5 h-5 text-green-500" />
                <span>Pago unico</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Right Visual (5 columns) -->
        <div class="lg:col-span-5 animate-slide-right delay-400">
          <div class="relative">
            <!-- Floating Cards Stack -->
            <div class="space-y-4">
              <!-- Card 1 -->
              <FloatingCard
                :icon="floatingCards[0].icon"
                :title="floatingCards[0].title"
                :subtitle="floatingCards[0].subtitle"
                :delay="floatingCards[0].delay"
                class="ml-8"
              />

              <!-- Card 2 -->
              <FloatingCard
                :icon="floatingCards[1].icon"
                :title="floatingCards[1].title"
                :subtitle="floatingCards[1].subtitle"
                :delay="floatingCards[1].delay"
                class="mr-8"
              />

              <!-- Card 3 -->
              <FloatingCard
                :icon="floatingCards[2].icon"
                :title="floatingCards[2].title"
                :subtitle="floatingCards[2].subtitle"
                :delay="floatingCards[2].delay"
                class="ml-4"
              />
            </div>

            <!-- Trust Badge -->
            <div class="absolute -bottom-6 -left-6 bg-green-50 border border-green-200 rounded-2xl px-5 py-3 shadow-lg">
              <div class="flex items-center gap-2">
                <CheckIcon class="w-5 h-5 text-green-600" />
                <span class="text-sm font-medium text-green-800">Servicio profesional RGPD</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
@reference "../../assets/styles/main.css";

/* Animations are defined in main.css */
/* .bg-organic, .animate-fade-up, .animate-slide-right, .delay-* classes */
</style>
